import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { usePermission } from '../context/PermissionContext';
import { useSortable, usePagination } from '../hooks/useTableUtils';
import { useAuth } from '../context/AuthContext';
import { measureTextWidth } from '../utils/textWidth';
import { fmtListDate } from '../utils/date';
import VersionCompareModal from '../components/VersionCompareModal';
import EstimateSummaryCard from '../components/EstimateSummaryCard';
import { STATUS_LABEL, isPublished, amountDelta } from '../utils/versionUtils';

const statusLabel = { draft:'임시저장', pending_approval:'승인대기', published:'발행', unapproved_published:'미승인발행' };
// tagOptions는 tags 상태에서 동적으로 생성

// 상태 열을 대신하는 견적번호 옆 아이콘 (마우스를 올리면 상태명 표시)
const statusIcon = {
  draft:                { ch:'✎', bg:'#f1f5f9', fg:'#64748b', bd:'#cbd5e1' },
  pending_approval:     { ch:'⏳', bg:'#fef9c3', fg:'#a16207', bd:'#fde68a' },
  published:            { ch:'✔', bg:'#dbeafe', fg:'#1d4ed8', bd:'#93c5fd' },
  unapproved_published: { ch:'!', bg:'#ffedd5', fg:'#c2410c', bd:'#fdba74' },
};
function StatusIcon({ status }) {
  const ic = statusIcon[status] || statusIcon.draft;
  const label = statusLabel[status] || status;
  return (
    <span title={label} aria-label={label}
      style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:18, height:18, marginLeft:6, borderRadius:'50%',
        fontSize:10, fontWeight:800, lineHeight:1, background:ic.bg, color:ic.fg, border:`1px solid ${ic.bd}`, verticalAlign:'middle' }}>{ic.ch}</span>
  );
}

// 목록 열 기본 너비(px). 견적번호는 내용이 더 길면 내용에 맞춰 늘어남
const DEFAULT_COL_W = { num: 235, recv: 161, enduser: 180 };

// 검색 결과에서 같은 그룹을 표시하는 색 띠 팔레트
const BAND_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ec4899', '#0ea5e9', '#a855f7', '#ef4444', '#14b8a6'];

export default function EstimateListPage({ businessType = 'goods' }) {
  const navigate = useNavigate();
  const isManpower = businessType === 'manpower';
  const newPath = isManpower ? '/service-estimate/new' : '/product-estimate/new';
  const { canPerformAction, isAdmin } = usePermission();
  const { user: authUser } = useAuth();
  const [estimates, setEstimates] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  // 견적서를 열었다가 브라우저 '뒤로 가기'로 돌아오면 직전 탭·검색·필터·페이지·펼침·스크롤 위치를 복원 (탭을 닫으면 사라지도록 sessionStorage)
  const VIEW_KEY = `estimateList.view.${businessType}`;
  const savedView = useRef(null);
  if (savedView.current === null) {
    try { savedView.current = JSON.parse(sessionStorage.getItem(VIEW_KEY) || 'null') || {}; } catch (e) { savedView.current = {}; }
  }
  const sv = savedView.current;
  const [activeTab, setActiveTab] = useState(sv.tab || 'mine');
  const [search, setSearch] = useState(sv.search || '');
  const [statusFilter, setStatusFilter] = useState(sv.status || '');
  const [tagFilter, setTagFilter] = useState(sv.tag || '');
  const [selected, setSelected] = useState(new Set());
  const [deleteModal, setDeleteModal] = useState(false); // true = bulk(selectedIds), number = single, number[] = specific ids
  const [versionPickGroup, setVersionPickGroup] = useState(null); // 다버전 그룹 선택 모달 — null 이면 닫힘
  const [pickedVersionIds, setPickedVersionIds] = useState(new Set());
  const [expandedIds, setExpandedIds] = useState(new Set(sv.expanded || [])); // 펼친 다버전 그룹(main.id)
  const [expandAll, setExpandAll] = useState(!!sv.expandAll);          // 모든 다버전 그룹 펼치기
  const [cmpPick, setCmpPick] = useState({ gid: null, ids: [] }); // 펼친 그룹 안에서 비교용으로 체크한 버전 id (최대 2)
  const [compareTarget, setCompareTarget] = useState(null);   // { a, b } 비교 모달
  // 견적번호 hover 요약 카드: 목록 데이터에는 항목이 없으므로 마우스를 올리면 해당 견적을 불러와 보여주고(캐시), 목록이 갱신되면 캐시를 비움
  const [hoverCard, setHoverCard] = useState(null);             // { est, rect }
  const [, bumpCard] = useState(0);
  const hoverTimer = useRef(null);
  const itemCache = useRef(new Map());
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [purgeModal, setPurgeModal] = useState(null); // number(단건) | number[](일괄) | null
  const [purgePassword, setPurgePassword] = useState('');
  const [purgeError, setPurgeError] = useState('');

  useEffect(() => { api.get('/settings/tags/all').then(setTags); }, []);

  const loadEstimates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ tab: activeTab, business_type: businessType });
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      if (tagFilter) params.set('tag', tagFilter);
      setEstimates(await api.get(`/estimates?${params}`));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [activeTab, search, statusFilter, tagFilter, businessType]);

  useEffect(() => { loadEstimates(); }, [loadEstimates]);
  useEffect(() => { itemCache.current.clear(); }, [estimates]);
  useEffect(() => () => clearTimeout(hoverTimer.current), []);
  // 모바일 환경(휴대폰·태블릿·좁은 화면): 견적번호를 한 번 누르면 요약, 같은 견적번호를 한 번 더 누르면 열기
  // 판정: 이번 입력이 터치이거나 / 호버·정밀 포인터가 없는 기기이거나 / 화면 폭이 좁을 때 (브라우저 창을 좁힌 경우 포함)
  const cardByTouch = useRef(false);
  const isTouchDevice = (ev) => {
    const pt = ev && (ev.pointerType || (ev.nativeEvent && ev.nativeEvent.pointerType));
    if (pt === 'touch' || pt === 'pen') return true;
    if (typeof window.matchMedia === 'function' && window.matchMedia('(hover: none), (pointer: coarse)').matches) return true;
    return window.innerWidth <= 820;
  };
  const openSummary = (est, rect) => {
    setHoverCard({ est, rect });
    if (!itemCache.current.has(est.id)) {
      itemCache.current.set(est.id, null);
      api.get(`/estimates/${est.id}`)
        .then(d => itemCache.current.set(est.id, d))
        .catch(() => itemCache.current.set(est.id, { error: true }))
        .finally(() => bumpCard(n => n + 1));
    }
  };
  const showSummary = (est) => (ev) => {
    if (isTouchDevice()) return; // 터치 기기의 가짜 mouseenter 는 무시 (클릭으로만 동작)
    const rect = ev.currentTarget.getBoundingClientRect();
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => openSummary(est, rect), 350);
  };
  const hideSummary = () => { clearTimeout(hoverTimer.current); cardByTouch.current = false; setHoverCard(null); };
  // 견적번호 클릭: 데스크톱은 바로 열기, 터치 기기는 1번째=요약 / 2번째(같은 견적번호)=열기
  const onNumClick = (est, open) => (ev) => {
    if (!isTouchDevice(ev)) { open(); return; }
    if (hoverCard && hoverCard.est.id === est.id) { hideSummary(); open(); return; }
    cardByTouch.current = true;
    openSummary(est, ev.currentTarget.getBoundingClientRect());
  };
  // 터치 기기: 견적번호 밖을 누르거나 스크롤하면 요약 닫기
  useEffect(() => {
    if (!hoverCard || !cardByTouch.current) return undefined;
    const close = (ev) => { if (!(ev.target.closest && ev.target.closest('[data-est-num]'))) setHoverCard(null); };
    const onScroll = () => setHoverCard(null);
    document.addEventListener('pointerdown', close, true);
    window.addEventListener('scroll', onScroll, true);
    return () => { document.removeEventListener('pointerdown', close, true); window.removeEventListener('scroll', onScroll, true); };
  }, [hoverCard]);
  useEffect(() => { setSelected(new Set()); }, [activeTab]);

  // 체크박스
  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const allMainIds = () => {
    return groupEstimates(estimates).map(g => g.main.id);
  };

  const toggleSelectAll = () => {
    const ids = allMainIds();
    if (ids.length > 0 && ids.every(id => selected.has(id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(ids));
    }
  };

  const selectedIds = [...selected];

  // 그룹으로 묶기: 목록에서 체크한 견적서(selected) + 펼친 그룹에서 체크한 버전(cmpPick)을 합쳐 하나의 그룹으로
  const mergeIds = [...new Set([...selectedIds, ...cmpPick.ids])].filter(id => estimates.some(x => x.id === id));
  const mergeBlockReason = (() => {
    if (mergeIds.length < 2) return '견적서를 2개 이상 체크하세요.';
    const rows = mergeIds.map(id => estimates.find(x => x.id === id)).filter(Boolean);
    if (rows.some(r => !(authUser && (authUser.uid === 'U-0000' || r.author_uid === authUser.uid)))) return '본인이 작성한 견적서만 묶을 수 있습니다.';
    if (new Set(rows.map(r => r.business_type)).size > 1) return '제품 견적서와 서비스 견적서는 함께 묶을 수 없습니다.';
    return '';
  })();
  // 선택 바 버튼들의 대상: 체크한 모든 견적서 (그룹이 달라도 됨)
  const pickRows = mergeIds.map(id => estimates.find(x => x.id === id)).filter(Boolean);
  const rootOfRow = (r) => r.parent_estimate_id || r.id;
  const sameGroup = pickRows.length > 0 && new Set(pickRows.map(rootOfRow)).size === 1;
  const byCreated = [...pickRows].sort((x, y) => new Date(x.created_at) - new Date(y.created_at) || x.id - y.id);
  const sideBySideReason = pickRows.length < 2 ? '2개 이상 체크하세요.' : pickRows.length > 4 ? `나란히 보기는 최대 4개입니다. (현재 ${pickRows.length}개)` : new Set(pickRows.map(r => r.business_type)).size > 1 ? '제품/서비스 견적서는 함께 볼 수 없습니다.' : '';
  const compareReason = pickRows.length !== 2 ? '같은 그룹의 견적서 2개를 체크하세요.' : !sameGroup ? '비교 분석은 같은 그룹의 두 견적서만 가능합니다.' : '';
  const splitReason = pickRows.length < 1 ? '분리할 견적서를 체크하세요.'
    : !sameGroup ? '같은 그룹의 견적서만 한 번에 분리할 수 있습니다.'
    : !pickRows.every(r => authUser && (authUser.uid === 'U-0000' || r.author_uid === authUser.uid)) ? '본인이 작성한 견적서만 분리할 수 있습니다.'
    : (!search.trim() && estimates.filter(x => rootOfRow(x) === rootOfRow(pickRows[0])).length <= pickRows.length) ? '그룹 전체는 분리할 수 없습니다. (남는 버전이 1개 이상 필요)' : '';
  const handleSideBySide = () => window.open(`/estimates/side-by-side?bt=${byCreated[0].business_type === 'manpower' ? 'manpower' : 'goods'}&ids=${byCreated.map(v => v.id).join(',')}`, '_blank');
  const handleOpenEach = () => {
    const blocked = byCreated.filter(v => !window.open(editPath(v), '_blank'));
    if (blocked.length > 0) alert(`브라우저가 팝업을 차단해 ${byCreated.length - blocked.length}개만 열렸습니다.\n\n주소창 오른쪽의 '팝업 차단됨' 아이콘에서 이 사이트의 팝업을 '항상 허용'으로 바꾼 뒤 다시 눌러 주세요.\n(미열림: ${blocked.map(v => v.estimate_number || v.draft_id).join(', ')})`);
  };
  const clearPicks = () => { setSelected(new Set()); setCmpPick({ gid: null, ids: [] }); };
  const handleSplit = async () => {
    if (!window.confirm(`선택한 ${pickRows.length}개 견적서를 현재 그룹에서 분리해 새 그룹으로 만듭니다.\n\n• 견적서 내용과 견적번호는 그대로이고, 소속 그룹과 버전 번호만 바뀝니다.\n• 양쪽 그룹의 버전 번호는 v1부터 다시 이어서 매겨집니다.\n\n계속할까요?`)) return;
    try { await api.post('/estimates/split', { ids: pickRows.map(r => r.id) }); clearPicks(); setExpandedIds(new Set()); loadEstimates(); }
    catch (err) { alert(err.message); }
  };
  const handleMerge = async () => {
    try {
      const pv = await api.post('/estimates/merge-preview', { ids: mergeIds });
      const lines = [`선택한 ${pv.order.length}개 견적서를 하나의 그룹으로 묶습니다.`, '', '■ 새 그룹의 버전 (작성일이 오래된 순)',
        ...pv.order.map(o => `  v${o.new_version}  ${o.estimate_number || ''}  (기존 v${o.from_version})`)];
      if (pv.excluded_from.length > 0) {
        lines.push('', '■ 아래 기존 그룹에서 제외됩니다 (남은 버전은 v1부터 다시 번호가 매겨집니다)');
        pv.excluded_from.forEach(g => lines.push(`  • ${g.label} 그룹: ${g.picked_versions.map(v => 'v' + v).join(', ')} 제외 → ${g.remaining_count}개 버전 남음`));
      }
      lines.push('', '견적서 내용과 견적번호는 바뀌지 않습니다. 계속할까요?');
      if (!window.confirm(lines.join('\n'))) return;
      await api.post('/estimates/merge', { ids: mergeIds });
      setSelected(new Set()); setCmpPick({ gid: null, ids: [] }); setExpandedIds(new Set());
      loadEstimates();
    } catch (err) { alert(err.message); }
  };


  // 단건 숨김/해제
  const handleUnhide = async (id) => { try { await api.put(`/estimates/${id}/unhide`); loadEstimates(); } catch(e) { alert(e.message); } };

  // 일괄 숨김
  const handleBulkHide = async () => {
    if (selectedIds.length === 0) return;
    try {
      await api.post('/estimates/bulk-hide', { ids: selectedIds });
      setSelected(new Set());
      loadEstimates();
    } catch (e) { alert(e.message); }
  };

  // 일괄 숨김 해제
  const handleBulkUnhide = async () => {
    if (selectedIds.length === 0) return;
    try {
      await api.post('/estimates/bulk-unhide', { ids: selectedIds });
      setSelected(new Set());
      loadEstimates();
    } catch (e) { alert(e.message); }
  };

  // 선택된(또는 단건) 견적서가 모두 draft 인지 검사 — true 면 비번 모달 없이 confirm 만으로 즉시 삭제
  const isAllDraft = (idsOrId) => {
    const ids = Array.isArray(idsOrId) ? idsOrId : [idsOrId];
    const map = new Map(estimates.map(e => [e.id, e.status]));
    return ids.length > 0 && ids.every(id => map.get(id) === 'draft');
  };

  // 단건 즉시 삭제 (draft 만)
  const handleQuickDeleteSingle = async (id) => {
    if (!window.confirm('이 견적서를 삭제하시겠습니까?')) return;
    try {
      await api.del(`/estimates/${id}`, {});
      loadEstimates();
    } catch (e) { alert(e.message); }
  };

  // 일괄 즉시 삭제 (모두 draft 일 때만)
  const handleQuickDeleteBulk = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`선택된 ${selectedIds.length}건을 삭제하시겠습니까?`)) return;
    try {
      await api.post('/estimates/bulk-delete', { ids: selectedIds });
      setSelected(new Set());
      loadEstimates();
    } catch (e) { alert(e.message); }
  };

  // 삭제 모달 열기 (bulk or single) — 발행 포함된 경우만 비번 모달 사용
  const openDeleteModal = (idOrBulk) => {
    if (idOrBulk === true) {
      if (isAllDraft(selectedIds)) return handleQuickDeleteBulk();
    } else {
      if (isAllDraft(idOrBulk)) return handleQuickDeleteSingle(idOrBulk);
    }
    setDeleteModal(idOrBulk); // true = bulk, number = single id
    setDeletePassword('');
    setDeleteError('');
  };

  // 삭제 실행 (비번 모달 경로)
  const handleDelete = async () => {
    if (!deletePassword) { setDeleteError('비밀번호를 입력하세요.'); return; }
    setDeleteError('');
    try {
      if (deleteModal === true) {
        await api.post('/estimates/bulk-delete', { ids: selectedIds, password: deletePassword });
        setSelected(new Set());
      } else if (Array.isArray(deleteModal)) {
        await api.post('/estimates/bulk-delete', { ids: deleteModal, password: deletePassword });
      } else {
        await api.del(`/estimates/${deleteModal}`, { password: deletePassword });
      }
      setDeleteModal(false);
      setDeletePassword('');
      loadEstimates();
    } catch (e) { setDeleteError(e.message); }
  };

  const togglePickedVersion = (id) => {
    setPickedVersionIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };
  const confirmVersionPick = async () => {
    const ids = Array.from(pickedVersionIds);
    if (ids.length === 0) return;
    const allDraft = ids.every(id => estimates.find(e => e.id === id)?.status === 'draft');
    setVersionPickGroup(null);
    if (allDraft) {
      if (!window.confirm(`선택된 ${ids.length}개 버전을 삭제하시겠습니까?`)) return;
      try {
        if (ids.length === 1) await api.del(`/estimates/${ids[0]}`, {});
        else await api.post('/estimates/bulk-delete', { ids });
        loadEstimates();
      } catch (e) { alert(e.message); }
    } else {
      setDeleteModal(ids);
      setDeletePassword('');
      setDeleteError('');
    }
  };

  // 단건 복원
  const handleRestore = async (id) => {
    try { await api.post(`/estimates/${id}/restore`); loadEstimates(); }
    catch (e) { alert(e.message); }
  };

  // 일괄 복원
  const handleBulkRestore = async () => {
    if (selectedIds.length === 0) return;
    try {
      await api.post('/estimates/bulk-restore', { ids: selectedIds });
      setSelected(new Set());
      loadEstimates();
    } catch (e) { alert(e.message); }
  };

  // 즉시삭제 — 삭제예정 상태의 견적서를 영구 삭제 (시스템관리자 전용, 비밀번호 검증)
  const openPurgeModal = (ids) => {
    if (!ids || ids.length === 0) return;
    setPurgeModal(ids);
    setPurgePassword('');
    setPurgeError('');
  };
  const handlePurge = async () => {
    if (!purgePassword) { setPurgeError('비밀번호를 입력하세요.'); return; }
    try {
      const r = await api.post('/estimates/bulk-purge', { ids: purgeModal, password: purgePassword });
      alert(r.message || '처리되었습니다.');
      setPurgeModal(null);
      setPurgePassword('');
      setSelected(new Set());
      loadEstimates();
    } catch (e) { setPurgeError(e.message); }
  };

  // 승인 취소
  const handleCancelApproval = async (id) => {
    if (!window.confirm('승인 요청을 취소하시겠습니까?')) return;
    try { await api.post(`/estimates/${id}/cancel-approval`); loadEstimates(); }
    catch (e) { alert(e.message); }
  };

  // 버전 그룹핑
  const groupEstimates = (list) => {
    const idMap = {};
    list.forEach(e => { idMap[e.id] = e; });

    const findRoot = (e) => {
      let current = e;
      const visited = new Set();
      while (current.parent_estimate_id && idMap[current.parent_estimate_id] && !visited.has(current.id)) {
        visited.add(current.id);
        current = idMap[current.parent_estimate_id];
      }
      return current.id;
    };

    const groups = {};
    list.forEach(e => {
      const rootId = findRoot(e);
      if (!groups[rootId]) groups[rootId] = [];
      groups[rootId].push(e);
    });

    const result = [];
    Object.values(groups).forEach(group => {
      group.sort((a, b) => b.version - a.version);
      result.push({ main: group[0], versions: group.slice(1) });
    });
    result.sort((a, b) => new Date(b.main.created_at) - new Date(a.main.created_at));
    // 검색 결과: 일치한 견적서를 모두 대표행 스타일로 한 줄씩(중복 없이) 보여 줌. 같은 그룹끼리 모으고 그룹 안에서는 최신 버전이 위
    // 같은 그룹은 _gkey 로 묶고, 일치 건이 2개 이상인 그룹에만 색 띠(_band)를 부여 (그룹마다 다른 색을 돌려 사용)
    if (search.trim()) {
      let bandNo = 0;
      return result.flatMap(g => {
        const members = [g.main, ...g.versions];
        const band = members.length > 1 ? BAND_COLORS[bandNo++ % BAND_COLORS.length] : null;
        return members.map(x => ({ main: x, versions: [], _gkey: g.main.id, _band: band, _gsize: members.length }));
      });
    }
    return result;
  };

  const grouped = groupEstimates(estimates);
  // 견적번호·일자 열 폭: 목록의 모든 데이터(상태 아이콘·인력/공유 배지 포함)가 잘리지 않도록 실제 글꼴로 측정해 결정
  const fixedColW = useMemo(() => {
    const fam = getComputedStyle(document.body).fontFamily;
    const m = (t, font) => measureTextWidth(t, `${font} ${fam}`);
    const PAD = 18; // 셀 좌우 패딩 16 + 여유 2
    let num = 0, date = 0;
    estimates.forEach(e => {
      let w = m(e.estimate_number || e.draft_id || '', '600 13px') + 24;                      // + 상태 아이콘(18) + 마진(6)
      if (e.business_type === 'manpower') w += m('인력', '700 10px') + 12 + 2 + 6;              // '인력' 배지
      if (Number(e.share_count) > 0) w += m(`🔗 ${e.share_count}`, '600 10px') + 14 + 2 + 6;   // 공유 배지
      num = Math.max(num, w);
      date = Math.max(date, m(fmtListDate(e.estimate_date || e.created_at), '400 13px'));
    });
    // 헤더 글자(+정렬 아이콘)도 들어가야 함
    num = Math.max(num, m('견적번호', '700 12px') + 28);
    date = Math.max(date, m('일자', '700 12px') + 28, m('00월 00일', '400 13px'));
    return { num: Math.ceil(num + PAD), date: Math.ceil(date + PAD) };
  }, [estimates]);
  const showAuthor = activeTab !== 'mine' && activeTab !== 'shared_by_me'; // 내 것만 보이는 탭에서는 작성자 열 불필요
  // 표의 실제 열 개수 (체크·견적번호·버전·일자·수신처·최종사용자·사업명·금액·작업 + 작성자). colSpan 이 이보다 크면 보이지 않는 열이 생겨 사업명 폭을 빼앗음
  // 작업 열 폭: 탭마다 노출되는 버튼(PDF·복사 / 복원·즉시삭제 / 숨김 해제 / PDF·복사·공유 거절)에 맞춤
  const actionColW = activeTab === 'shared' ? 196 : activeTab === 'deleted' ? 148 : activeTab === 'hidden' ? 100 : 128;
  const tableColCount = showAuthor ? 10 : 9;

  // 이 견적서를 삭제할 수 있는가 (서버 규칙과 동일): 작성자 본인(또는 시스템최고관리자) 이고,
  // 임시저장이면 권한 없이 가능 / 그 외(발행·승인대기)는 '견적서 삭제' 권한 필요
  const canDeleteEst = (est) => {
    if (!authUser) return false;
    if (!(authUser.uid === 'U-0000' || est.author_uid === authUser.uid)) return false;
    return est.status === 'draft' || canPerformAction(est.business_type === 'manpower' ? '서비스견적서 삭제' : '제품 견적서 삭제');
  };

  // 다버전 그룹 펼침: 전체 펼치기 토글 또는 개별 토글. 검색 중에는 다버전 그룹을 자동으로 펼침(이전 버전 매칭을 바로 볼 수 있게)
  const isExpanded = (g) => g.versions.length > 0 && (expandAll || !!search || expandedIds.has(g.main.id));
  // 한 번에 하나만 펼침: 다른 행을 펼치면 기존에 펼친 행은 자동으로 닫힘 (이미 펼친 행을 다시 누르면 접힘)
  const toggleExpand = (g) => setExpandedIds(prev => (prev.has(g.main.id) ? new Set() : new Set([g.main.id])));
  const editPath = (v) => `/${v.business_type === 'manpower' ? 'service-estimate' : 'product-estimate'}/new?id=${v.id}`;
  const toggleCmp = (g, id) => setCmpPick(prev => {
    // 검색 중에는 모든 그룹이 펼쳐져 있으므로 서로 다른 그룹의 버전도 함께 체크할 수 있음 (그룹으로 묶기용). 평소에는 한 그룹만.
    const cur = (prev.gid === g.main.id || search) ? prev.ids : [];
    const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]; // 비교는 정확히 2개일 때, 새 창 열기는 2개 이상일 때 사용
    return { gid: g.main.id, ids: next };
  });
  const multiGroupCount = grouped.filter(g => g.versions.length > 0).length;

  // ---- 열 너비 드래그 조절 ----
  // 사업명 열이 남는 폭을 흡수하는 구조라서, 사업명 왼쪽 열은 오른쪽 경계를, 오른쪽 열은 왼쪽 경계를 끌어 넓히면 사업명이 그만큼 줄어든다.
  // 견적번호·일자·금액은 데이터가 잘리지 않는 최소 폭(자동 계산값) 아래로는 줄일 수 없다. 설정은 브라우저에 저장.
  const NAME_MIN_W = 100;
  const COL_STORE_KEY = 'estimateList.colWidths.v1';
  const [colOverride, setColOverride] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COL_STORE_KEY) || '{}') || {}; } catch (e) { return {}; }
  });
  useEffect(() => { try { localStorage.setItem(COL_STORE_KEY, JSON.stringify(colOverride)); } catch (e) { /* 저장 불가 환경은 무시 */ } }, [colOverride]);

  // 금액 열 폭: 모든 금액과 버전별 증감 텍스트(금액 아래 줄)가 잘리지 않도록 실제 글꼴로 측정.
  // 펼침/접힘 상태와 무관하게 항상 같은 값 → 펼쳐도 열 너비가 변하지 않음
  const amountColW = (() => {
    const fam = getComputedStyle(document.body).fontFamily;
    const m = (t, font) => measureTextWidth(t, `${font} ${fam}`);
    let w = m('금액', '700 12px') + 28; // 헤더 글자 + 정렬 아이콘
    grouped.forEach(g => {
      const all = [g.main, ...g.versions];
      all.forEach((v, i) => {
        let cell = m(`₩${Number(v.total_amount || 0).toLocaleString()}`, '700 13px');
        const prev = all[i + 1];
        const delta = prev ? amountDelta(v.total_amount, prev.total_amount, false) : null;
        if (delta) cell = Math.max(cell, m(delta.text, '600 10px')); // 증감은 금액 아래 줄 → 둘 중 넓은 쪽
        w = Math.max(w, cell + 18);                        // 셀 좌우 패딩 16 + 여유 2
      });
    });
    return Math.ceil(w);
  })();

  const colMin = { num: fixedColW.num, ver: 80, date: fixedColW.date, recv: 60, enduser: 60, amt: amountColW, author: 50 };
  const colWidth = (key, def) => (colOverride[key] != null ? Math.max(colOverride[key], colMin[key]) : def);
  const resetCol = (key) => setColOverride(prev => { const n = { ...prev }; delete n[key]; return n; });
  const startColResize = (ev, key, side) => {
    ev.preventDefault(); ev.stopPropagation();
    const th = ev.currentTarget.closest('th');
    const nameTh = th.parentElement.children[6]; // 체크·견적번호·버전·일자·수신처·최종사용자·사업명
    const startX = ev.clientX;
    const startW = th.getBoundingClientRect().width;
    const maxW = startW + nameTh.getBoundingClientRect().width - NAME_MIN_W;
    const dir = side === 'right' ? 1 : -1;
    const move = (e) => {
      const w = Math.round(Math.max(colMin[key], Math.min(maxW, startW + dir * (e.clientX - startX))));
      setColOverride(prev => (prev[key] === w ? prev : { ...prev, [key]: w }));
    };
    const up = () => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up);
      document.body.style.userSelect = ''; document.body.style.cursor = '';
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up); window.addEventListener('pointercancel', up);
    document.body.style.userSelect = 'none'; document.body.style.cursor = 'col-resize';
  };
  const resizeHandle = (key, side) => (
    <span className="col-resize-handle" title="드래그해서 열 너비 조절 (더블클릭: 이 열 초기화)"
      onPointerDown={(ev) => startColResize(ev, key, side)} onClick={(ev) => ev.stopPropagation()} onDoubleClick={(ev) => { ev.stopPropagation(); resetCol(key); }}
      style={{ [side]: 0 }} />
  );

  // 정렬 (main 기준 flat 변환 후 정렬)
  const flatForSort = grouped.map(g => ({ ...g.main, _group: g }));
  const { sorted: sortedFlat, toggleSort, SortIcon } = useSortable(flatForSort, null);
  const sortedGrouped = sortedFlat.map(f => f._group);

  // 페이지네이션
  const { paged: pagedGrouped, Pager, page: curPage, setPage } = usePagination(sortedGrouped, 20);
  // 뒤로 가기 복원: 처음 데이터가 로드되면 저장해 둔 페이지·스크롤 위치로 이동
  const viewRestored = useRef(false);
  useEffect(() => {
    if (loading || viewRestored.current) return;
    viewRestored.current = true;
    if (sv.page > 1) setPage(sv.page);
    if (sv.scrollY > 0) setTimeout(() => window.scrollTo(0, sv.scrollY), 50);
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps
  const viewState = useRef({});
  viewState.current = { tab: activeTab, search, status: statusFilter, tag: tagFilter, page: viewRestored.current ? curPage : (sv.page || 1), expanded: [...expandedIds], expandAll };
  useEffect(() => {
    const save = () => { try { sessionStorage.setItem(VIEW_KEY, JSON.stringify({ ...viewState.current, scrollY: window.scrollY })); } catch (e) { /* 저장 불가 환경은 무시 */ } };
    save(); // 상태가 바뀔 때마다 저장
    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, [VIEW_KEY, activeTab, search, statusFilter, tagFilter, curPage, expandedIds, expandAll]);

  const allTagNames = tags.map(t => t.name);
  const allChecked = grouped.length > 0 && grouped.every(g => selected.has(g.main.id));


  const deleteIdsList = deleteModal === true ? selectedIds : Array.isArray(deleteModal) ? deleteModal : (typeof deleteModal === 'number' ? [deleteModal] : []);
  const deleteCount = deleteIdsList.length;

  return (
    <div>
      {/* Tabs */}
      <div className="tabs">
        {[
          { key:'mine', label:'본인' },{ key:'all', label:'전체 견적서' },{ key:'dept', label:'부서 견적서' },{ key:'shared', label:'공유받은 견적서' },{ key:'shared_by_me', label:'공유한 견적서' },{ key:'hidden', label:'숨김처리된 견적서' },{ key:'deleted', label:'삭제예정 견적서' }
        ].map(t => (
          <div key={t.key} className={`tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}>{t.label}</div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <div className="search-box"><span>🔍</span><input placeholder="견적번호, 수신처, 수신자, 사업명, 최종사용자, 견적항목, 세부사항, 내부 메모, 버전 메모 검색..." value={search} onChange={e => setSearch(e.target.value)} />
            {search && (
              <button type="button" aria-label="검색어 지우기" title="검색어 지우기"
                onClick={ev => { setSearch(''); ev.currentTarget.parentElement.querySelector('input')?.focus(); }}
                style={{ flex:'none', width:20, height:20, padding:0, border:'none', borderRadius:'50%', background:'#cbd5e1', color:'#fff', fontSize:11, lineHeight:'20px', cursor:'pointer' }}>✕</button>
            )}
          </div>
          <select className="form-input" style={{ width:110, padding:'7px 10px', fontSize:12 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">전체 상태</option>
            <option value="draft">임시저장</option>
            <option value="pending_approval">승인대기</option>
            <option value="published">발행</option>
            <option value="unapproved_published">미승인발행</option>
          </select>
          <select className="form-input" style={{ width:100, padding:'7px 10px', fontSize:12 }} value={tagFilter} onChange={e => setTagFilter(e.target.value)}>
            <option value="">전체 태그</option>
            {allTagNames.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {multiGroupCount > 0 && (
            <label style={{ fontSize:12, color:'#475569', cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}
              title="여러 버전이 있는 견적의 버전 목록을 모두 펼칩니다">
              <input type="checkbox" checked={expandAll} onChange={e => setExpandAll(e.target.checked)} />
              모든 버전 펼치기 <span style={{ color:'#94a3b8' }}>({multiGroupCount})</span>
            </label>
          )}
          {Object.keys(colOverride).length > 0 && (
            <button className="btn btn-outline btn-sm" title="드래그로 바꾼 열 너비를 기본값으로 되돌립니다" onClick={() => setColOverride({})}>↔ 열 너비 초기화</button>
          )}
        </div>
        <div className="btn-group">
          {canPerformAction(isManpower ? '서비스견적서 생성·수정' : '제품 견적서 생성·수정') && (
            <button className="btn btn-primary btn-sm" onClick={() => navigate(newPath)}>✚ {isManpower ? '서비스' : '제품'} 견적서 작성</button>
          )}
        </div>
      </div>

      {/* 선택 액션바 */}
      {(selectedIds.length > 0 || cmpPick.ids.length > 0) && (
        <div style={{
          display:'flex', alignItems:'center', gap:10, padding:'8px 16px',
          background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8, marginBottom:8,
          fontSize:13
        }}>
          <span style={{ fontWeight:600, color:'#1e40af' }}>{selectedIds.length > 0 ? `${selectedIds.length}건 선택` : ''}{cmpPick.ids.length > 0 ? `${selectedIds.length > 0 ? ' + ' : ''}버전 ${cmpPick.ids.length}개 체크` : ''}</span>
          <div style={{ flex:1 }} />
          {!sideBySideReason && (
            <button className="btn btn-outline btn-sm" onClick={handleSideBySide}
              title="체크한 견적서를 한 화면에 나란히 표시합니다 (최대 4개)">▥ 나란히 보기</button>
          )}
          {pickRows.length >= 2 && (
            <button className="btn btn-outline btn-sm" onClick={handleOpenEach}
              title="체크한 견적서를 각각 새 창(탭)에서 엽니다">↗ 각각 새창에서 열기</button>
          )}
          {/* 비교 분석·그룹에서 분리·그룹으로 묶기는 사용할 수 있을 때만 표시 */}
          {!compareReason && (
            <button className="btn btn-outline btn-sm" onClick={() => setCompareTarget({ a: byCreated[0].id, b: byCreated[1].id })}
              title="체크한 두 견적서를 비교합니다">⇄ 비교 분석</button>
          )}
          {activeTab !== 'deleted' && activeTab !== 'shared' && (
            <>
              {!splitReason && (
                <button className="btn btn-outline btn-sm" onClick={handleSplit}
                  title="체크한 견적서를 현재 그룹에서 분리해 새 그룹으로 만듭니다 (내용은 변경되지 않음)">✂ 그룹에서 분리</button>
              )}
              {!mergeBlockReason && (
                <button className="btn btn-outline btn-sm" onClick={handleMerge}
                  title="체크한 견적서를 하나의 그룹으로 묶습니다 (내용은 변경되지 않음)">🔗 그룹으로 묶기</button>
              )}
            </>
          )}
          {selectedIds.length === 0 ? null : activeTab === 'deleted' ? (
            <>
              <button className="btn btn-outline btn-sm" onClick={handleBulkRestore}>일괄 복원</button>
              {isAdmin && (
                <button className="btn btn-sm" style={{ background:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca' }}
                  onClick={() => openPurgeModal(selectedIds)}>일괄 즉시삭제</button>
              )}
            </>
          ) : activeTab === 'hidden' ? (
            <button className="btn btn-outline btn-sm" onClick={handleBulkUnhide}>일괄 숨김 해제</button>
          ) : (
            <>
              <button className="btn btn-outline btn-sm" onClick={handleBulkHide}>일괄 숨김</button>
              {(() => {
                // 선택된 항목 중 삭제 가능한 견적서가 하나라도 있을 때만 노출
                const anyDeletable = estimates.some(e => selected.has(e.id) && canDeleteEst(e));
                return anyDeletable && (
                  <button className="btn btn-sm" style={{ background:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca' }}
                    onClick={() => openDeleteModal(true)}>일괄 삭제</button>
                );
              })()}
            </>
          )}
          <button className="btn btn-outline btn-sm" onClick={clearPicks}
            style={{ fontSize:11, padding:'3px 8px', color:'#64748b' }}>선택 해제</button>
        </div>
      )}

      {/* Table */}
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center' }}>로딩 중...</div> : (
          <div style={{ overflowX:'auto' }}>
            <table className="est-list">
              {/* 고정 레이아웃: 좁은 열은 px, 수신처는 %, 사업명이 나머지를 모두 차지 → 창 폭을 넘지 않음 */}
              <colgroup>
                <col style={{ width:32 }} /><col style={{ width:colWidth('num', Math.max(fixedColW.num, DEFAULT_COL_W.num)) }} /><col style={{ width:colWidth('ver', 112) }} /><col style={{ width:colWidth('date', fixedColW.date) }} />
                <col style={{ width:colWidth('recv', DEFAULT_COL_W.recv) }} /><col style={{ width:colWidth('enduser', DEFAULT_COL_W.enduser) }} /><col />
                <col style={{ width:colWidth('amt', amountColW) }} />
                {showAuthor && <col style={{ width:colWidth('author', 60) }} />}
                <col style={{ width:actionColW }} />
              </colgroup>
              <thead><tr>
                <th style={{textAlign:'center'}}>
                  <input type="checkbox" checked={allChecked} onChange={toggleSelectAll} />
                </th>
                <th onClick={()=>toggleSort('estimate_number')}>견적번호 <SortIcon col="estimate_number"/>{resizeHandle('num','right')}</th><th>버전{resizeHandle('ver','right')}</th><th onClick={()=>toggleSort('estimate_date')}>일자 <SortIcon col="estimate_date"/>{resizeHandle('date','right')}</th><th onClick={()=>toggleSort('receiver_company')}>수신처 <SortIcon col="receiver_company"/>{resizeHandle('recv','right')}</th><th onClick={()=>toggleSort('end_user')}>최종사용자 <SortIcon col="end_user"/>{resizeHandle('enduser','right')}</th><th onClick={()=>toggleSort('project_name')}>사업명 <SortIcon col="project_name"/></th>
                <th style={{textAlign:'right'}} onClick={()=>toggleSort('total_amount')}>{resizeHandle('amt','left')}금액 <SortIcon col="total_amount"/></th>{showAuthor && <th onClick={()=>toggleSort('author_name')}>{resizeHandle('author','left')}작성자 <SortIcon col="author_name"/></th>}<th>작업</th>
              </tr></thead>
              <tbody>
                {pagedGrouped.map((g, gi) => {
                  const e = g.main;
                  const groupStart = g._gkey != null && gi > 0 && pagedGrouped[gi - 1]._gkey !== g._gkey; // 그룹 경계 (검색 결과)
                                    const isSelected = selected.has(e.id);
                  return (
                    <React.Fragment key={e.id}>
                      <tr className={groupStart ? 'grp-start' : undefined} style={isSelected ? { background:'#eff6ff' } : undefined}>
                        <td title={g._band ? `같은 그룹 · 검색에 ${g._gsize}개 일치` : undefined} style={{ textAlign:'center', whiteSpace:'nowrap', ...(g._band ? { boxShadow:`inset 4px 0 0 ${g._band}` } : null) }}>
                          <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(e.id)} />
                        </td>
                        <td className="clickable" data-est-num onMouseEnter={showSummary(e)} onMouseLeave={hideSummary} onClick={onNumClick(e, () => navigate(`/${e.business_type==='manpower'?'service-estimate':'product-estimate'}/new?id=${e.id}`))}>
                          {e.business_type === 'manpower' && (
                            <span style={{marginRight:6,padding:'1px 6px',fontSize:10,fontWeight:700,background:'#fef3c7',color:'#92400e',borderRadius:6,border:'1px solid #fde68a'}}>인력</span>
                          )}
                          {e.estimate_number || e.draft_id}
                          <StatusIcon status={e.status} />
                          {Number(e.share_count) > 0 && (
                            <span title={`${e.share_count}명에게 공유 중${e.shared_with?.length ? `: ${e.shared_with.join(', ')}` : ''}`}
                              onClick={(ev)=>{ev.stopPropagation(); navigate(`/${e.business_type==='manpower'?'service-estimate':'product-estimate'}/new?id=${e.id}`);}}
                              style={{ marginLeft:6, padding:'1px 7px', fontSize:10, fontWeight:600, background:'#fef3c7', color:'#92400e', borderRadius:8, border:'1px solid #fde68a', cursor:'pointer' }}>
                              🔗 {e.share_count}
                            </span>
                          )}
                        </td>
                        <td>
                          {(() => {
                            const multi = g.versions.length > 0;
                            const open = multi && isExpanded(g);
                            return (
                              <div style={{ display:'flex', alignItems:'center', gap:4, minWidth:0 }}>
                                <span title={`${STATUS_LABEL[e.status] || e.status}${g.versions.length > 0 || !search.trim() ? ' · 최신 버전' : ''}`}
                                  style={{ flex:'none', padding:'2px 8px', fontSize:11, fontWeight:700, background:'#3b82f6', color:'#fff', borderRadius:10, display:'inline-flex', alignItems:'center', gap:4 }}>
                                  v{e.version}
                                </span>
                                {multi && (
                                  <button type="button" onClick={() => toggleExpand(g)}
                                    title={open ? '이전 버전 목록 접기' : '이전 버전 목록 펼치기'}
                                    style={{ flex:'none', padding:'2px 9px', fontSize:11, fontWeight:600, borderRadius:10, cursor:'pointer', whiteSpace:'nowrap',
                                      border:`1px solid ${open ? '#3b82f6' : '#cbd5e1'}`, background:open ? '#eff6ff' : '#fff', color:open ? '#1d4ed8' : '#475569' }}>
                                    외 {g.versions.length}개 {open ? '▴' : '▾'}
                                  </button>
                                )}
                                {e.version_note && (
                                  <span title={e.version_note} style={{ flex:'1 1 auto', minWidth:0, fontSize:10, color:'#64748b', fontStyle:'italic', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>“{e.version_note}”</span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td>{fmtListDate(e.estimate_date || e.created_at)}</td>
                        <td title={e.receiver_company || ''}>{e.receiver_company || '-'}</td>
                        <td title={e.end_user || ''}>{e.end_user || '-'}</td>
                        <td title={e.project_name || ''}>{e.project_name || '-'}</td>
                        <td style={{ textAlign:'right', fontWeight:700 }}>₩{Number(e.total_amount||0).toLocaleString()}</td>
                        {showAuthor && <td>{e.author_name || '-'}</td>}
                        <td className="col-actions"><div className="btn-group">
                          {activeTab === 'deleted' ? (
                            <>
                              <button className="btn btn-outline btn-sm" onClick={() => handleRestore(e.id)}>복원</button>
                              {isAdmin && (
                                <button className="btn btn-sm" style={{ background:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca' }}
                                  onClick={() => openPurgeModal([e.id])}>즉시삭제</button>
                              )}
                            </>
                          ) : activeTab === 'hidden' ? (
                            <button className="btn btn-outline btn-sm" onClick={() => handleUnhide(e.id)}>숨김 해제</button>
                          ) : activeTab === 'shared' ? (
                            <>
                              {(e.status === 'published' || e.status === 'unapproved_published') && (
                                <>
                                  <button className="btn btn-outline btn-sm" onClick={() => window.open(`/estimates/print?id=${e.id}`,'_blank')}>PDF</button>
                                  {canPerformAction(e.business_type==='manpower' ? '서비스견적서 생성·수정' : '제품 견적서 생성·수정') && (
                                    <button className="btn btn-outline btn-sm" onClick={() => navigate(`/${e.business_type==='manpower'?'service-estimate':'product-estimate'}/new?copy=${e.id}`)}>복사</button>
                                  )}
                                </>
                              )}
                              <button className="btn btn-sm" style={{ background:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca' }}
                                onClick={async () => {
                                  if (!window.confirm('이 견적서의 공유를 거절하시겠습니까? 목록에서 즉시 사라집니다.')) return;
                                  try { await api.del(`/estimates/${e.id}/shares/me`); loadEstimates(); }
                                  catch (err) { alert(err.message); }
                                }}>공유 거절</button>
                            </>
                          ) : (
                            <>
                              {e.status === 'pending_approval' && <button className="btn btn-outline btn-sm" onClick={() => handleCancelApproval(e.id)}>취소</button>}
                              {(e.status === 'published' || e.status === 'unapproved_published') && (
                                <>
                                  <button className="btn btn-outline btn-sm" onClick={() => window.open(`/estimates/print?id=${e.id}`,'_blank')}>PDF</button>
                                  {canPerformAction(e.business_type==='manpower' ? '서비스견적서 생성·수정' : '제품 견적서 생성·수정') && (
                                    <button className="btn btn-outline btn-sm" onClick={() => navigate(`/${e.business_type==='manpower'?'service-estimate':'product-estimate'}/new?copy=${e.id}`)}>복사</button>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </div></td>
                      </tr>
                      {isExpanded(g) && (() => {
                        const all = [e, ...g.versions]; // 최신 → 과거
                        const picked = cmpPick.ids.filter(id => all.some(x => x.id === id)); // 이 그룹에서 체크한 버전
                        const colCount = tableColCount;
                        // 펼친 영역: 일반 행과 구분되는 연한 인디고 배경 + 왼쪽 강조선 (체크된 버전은 한 단계 진하게)
                        const accent = { boxShadow:'inset 3px 0 0 #818cf8' }; // border 는 표 폭 계산에 영향을 주므로 안쪽 그림자로 표현
                        return (
                          <>
                            <tr className="version-sub">
                              <td colSpan={colCount} style={{ padding:'8px 16px 4px 56px', ...accent }}>
                                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                  <span style={{ fontSize:12, fontWeight:700, color:'#475569' }}>버전 이력 ({all.length})</span>
                                </div>
                              </td>
                            </tr>
                            {/* 버전별 행 — 접힌 목록과 같은 열 순서: 체크 | 견적번호 | 버전 | 일자 | 수신처 | 사업명 | 금액 | 상태 | 태그 | (작성자) | 작업 */}
                            {all.map((v, i) => {
                              const prev = all[i + 1];
                              const delta = prev ? amountDelta(v.total_amount, prev.total_amount, false) : null; // 목록에서는 % 없이 금액 증감만
                              const canCreate = canPerformAction(v.business_type === 'manpower' ? '서비스견적서 생성·수정' : '제품 견적서 생성·수정');
                              return (
                                <tr key={v.id} className={`version-sub${picked.includes(v.id) ? ' picked' : ''}`}>
                                  <td style={{ textAlign:'center', ...accent }}>
                                    <input type="checkbox" title="비교/나란히 보기용 선택" checked={picked.includes(v.id)} onChange={() => toggleCmp(g, v.id)} />
                                  </td>
                                  <td className="clickable" data-est-num style={{ whiteSpace:'nowrap' }} onMouseEnter={showSummary(v)} onMouseLeave={hideSummary} onClick={onNumClick(v, () => navigate(editPath(v)))}>{v.estimate_number || v.draft_id || '-'}<StatusIcon status={v.status} /></td>
                                  <td style={{ fontWeight:700 }}>
                                    <div style={{ display:'flex', alignItems:'center', gap:4, minWidth:0 }}>
                                      <span style={{ flex:'none' }}>v{v.version}{i === 0 && <span style={{ marginLeft:4, fontSize:9, color:'#3b82f6' }}>최신</span>}</span>
                                      {v.version_note && <span title={v.version_note} style={{ flex:'1 1 auto', minWidth:0, fontSize:10, fontWeight:400, color:'#64748b', fontStyle:'italic', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>“{v.version_note}”</span>}
                                    </div>
                                  </td>
                                  <td>{fmtListDate(v.estimate_date || v.created_at)}</td>
                                  <td title={v.receiver_company || ''}>{v.receiver_company || '-'}</td>
                                  <td title={v.end_user || ''}>{v.end_user || '-'}</td>
                                  <td title={v.project_name || ''}>
                                    <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{v.project_name || '-'}</div>
                                  </td>
                                  <td style={{ textAlign:'right', fontWeight:700, paddingTop:3, paddingBottom:3 }}>
                                    {/* 증감은 금액 아래 줄에 표시 — 세로 패딩을 줄여 두 줄이 들어가도 행 높이는 다른 행과 같음 */}
                                    <div style={{ lineHeight:'17px' }}>₩{Number(v.total_amount||0).toLocaleString()}</div>
                                    {delta && <div style={{ fontSize:10, fontWeight:600, lineHeight:'12px', color:delta.color }} title={`v${prev.version} 대비`}>{delta.text}</div>}
                                  </td>
                                  {showAuthor && <td>{v.author_name || '-'}</td>}
                                  <td className="col-actions"><div className="btn-group">
                                    {isPublished(v.status) && <button className="btn btn-outline btn-sm" onClick={() => window.open(`/estimates/print?id=${v.id}`, '_blank')}>PDF</button>}
                                    {isPublished(v.status) && canCreate && activeTab !== 'deleted' && (
                                      <button className="btn btn-outline btn-sm" title="이 버전을 복사해 새 견적서로 작성"
                                        onClick={() => navigate(`/${v.business_type === 'manpower' ? 'service-estimate' : 'product-estimate'}/new?copy=${v.id}`)}>복사</button>
                                    )}
                                  </div></td>
                                </tr>
                              );
                            })}
                          </>
                        );
                      })()}
                    </React.Fragment>
                  );
                })}
                {grouped.length === 0 && <tr><td colSpan={tableColCount} style={{ textAlign:'center', padding:32, color:'#94a3b8' }}>견적서가 없습니다.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        <Pager />
      </div>

      {hoverCard && <EstimateSummaryCard est={hoverCard.est} data={itemCache.current.get(hoverCard.est.id)} rect={hoverCard.rect} />}
      {compareTarget && <VersionCompareModal aId={compareTarget.a} bId={compareTarget.b} onClose={() => setCompareTarget(null)} />}

      {/* 버전 선택 삭제 모달 */}
      {versionPickGroup && (() => {
        const allVersions = [versionPickGroup.main, ...versionPickGroup.versions]
          .sort((a,b) => (b.version||0) - (a.version||0));
        const allIds = allVersions.map(v=>v.id);
        const allChecked = allIds.every(id => pickedVersionIds.has(id));
        return (
          <div className="modal-overlay" onClick={() => setVersionPickGroup(null)}>
            <div className="modal" style={{ width:460 }} onClick={ev => ev.stopPropagation()}>
              <div className="modal-header">
                <h3>어느 버전을 삭제할까요?</h3>
                <button className="modal-close" onClick={() => setVersionPickGroup(null)}>✕</button>
              </div>
              <div className="modal-body">
                <div style={{ fontSize:12, color:'#64748b', marginBottom:10 }}>
                  같은 견적의 버전이 {allVersions.length}개 있습니다. 삭제할 버전을 선택하세요.
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 10px', background:'#f8fafc', borderRadius:6, marginBottom:6 }}>
                  <label style={{ fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                    <input type="checkbox" checked={allChecked}
                      onChange={() => setPickedVersionIds(allChecked ? new Set() : new Set(allIds))} />
                    전체 {allChecked ? '해제' : '선택'}
                  </label>
                  <span style={{ fontSize:11, color:'#94a3b8' }}>선택됨 {pickedVersionIds.size} / {allVersions.length}</span>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:280, overflowY:'auto' }}>
                  {allVersions.map(v => {
                    const isMain = v.id === versionPickGroup.main.id;
                    const checked = pickedVersionIds.has(v.id);
                    return (
                      <label key={v.id} style={{
                        display:'flex', alignItems:'center', gap:10, padding:'8px 10px',
                        border:'1px solid '+(checked?'#fecaca':'#e2e8f0'), borderRadius:6,
                        background: checked?'#fef2f2':'#fff', cursor:'pointer'
                      }}>
                        <input type="checkbox" checked={checked} onChange={() => togglePickedVersion(v.id)} />
                        <span style={{ fontSize:12, fontWeight:700, minWidth:24 }}>v{v.version}</span>
                        {isMain && <span style={{ fontSize:10, padding:'1px 6px', background:'#dbeafe', color:'#1e40af', borderRadius:8 }}>최신</span>}
                        <span style={{ fontSize:11, color:'#64748b', minWidth:60 }}>{(v.estimate_date||v.created_at||'').slice(0,10)}</span>
                        <span style={{ fontSize:11, color:'#64748b', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{v.estimate_number || v.draft_id}</span>
                        <span style={{ fontSize:11, color:'#64748b' }}>₩{Number(v.total_amount||0).toLocaleString()}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline" onClick={() => setVersionPickGroup(null)}>취소</button>
                <button className="btn" style={{ background:'#dc2626', color:'#fff' }}
                  disabled={pickedVersionIds.size === 0}
                  onClick={confirmVersionPick}>선택 {pickedVersionIds.size}개 삭제</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 즉시삭제(영구) 확인 모달 — 시스템관리자 전용 */}
      {purgeModal && (
        <div className="modal-overlay" onClick={() => setPurgeModal(null)}>
          <div className="modal" style={{ width:400 }} onClick={ev => ev.stopPropagation()}>
            <div className="modal-header">
              <h3>견적서 즉시삭제 (영구)</h3>
              <button className="modal-close" onClick={() => setPurgeModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="alert alert-danger" style={{ marginBottom:16 }}>
                ⚠ {purgeModal.length}건의 견적서를 즉시 영구 삭제합니다.<br/>
                이 작업은 되돌릴 수 없습니다.
              </div>
              <div className="form-group">
                <label className="form-label">비밀번호 확인</label>
                <input className="form-input" type="password" placeholder="비밀번호를 입력하세요"
                  value={purgePassword} onChange={ev => { setPurgePassword(ev.target.value); setPurgeError(''); }}
                  onKeyDown={ev => ev.key === 'Enter' && handlePurge()} autoFocus />
              </div>
              {purgeError && <div style={{ color:'#dc2626', fontSize:12, marginTop:4 }}>{purgeError}</div>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setPurgeModal(null)}>취소</button>
              <button className="btn" style={{ background:'#dc2626', color:'#fff' }} onClick={handlePurge}>즉시삭제</button>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {deleteModal !== false && (
        <div className="modal-overlay" onClick={() => setDeleteModal(false)}>
          <div className="modal" style={{ width:400 }} onClick={ev => ev.stopPropagation()}>
            <div className="modal-header">
              <h3>견적서 삭제</h3>
              <button className="modal-close" onClick={() => setDeleteModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="alert alert-danger" style={{ marginBottom:16 }}>
                {deleteCount}건의 견적서가 삭제 예정으로 전환됩니다.<br/>
                7일 후 완전 삭제되며, 그 전에 복원할 수 있습니다.
              </div>
              <div className="form-group">
                <label className="form-label">비밀번호 확인</label>
                <input className="form-input" type="password" placeholder="비밀번호를 입력하세요"
                  value={deletePassword} onChange={ev => { setDeletePassword(ev.target.value); setDeleteError(''); }}
                  onKeyDown={ev => ev.key === 'Enter' && handleDelete()} autoFocus />
              </div>
              {deleteError && <div style={{ color:'#dc2626', fontSize:12, marginTop:4 }}>{deleteError}</div>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setDeleteModal(false)}>취소</button>
              <button className="btn" style={{ background:'#dc2626', color:'#fff' }} onClick={handleDelete}>삭제</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// React import for Fragment
const React = require('react');
